<script setup lang="ts">
import {
  selectContentAction,
  selectContentEvent
} from "~/utils/analytics/selectContent"
import {
  BRANDS_MODAL,
  CATEGORIES_MODAL,
  FILTERS_MODAL,
  FILTER_ACTIONS
} from "~/utils/filters"

const { switchState: openFilters } = useUIState(FILTERS_MODAL)
const { switchState: openBrands } = useUIState(BRANDS_MODAL)
const { switchState: openCategories } = useUIState(CATEGORIES_MODAL)
const { t } = useI18n()
const route = useRoute()

const facetNames = ["inStock", "deductibleProduct"]

const { event, sendEvent } = useGAEvent(
  `track_select_content_available_`,
  "select_content"
)

const algoliaReset = useAlgoliaReset()
const { scopedId } = useAlgoliaWidgetScope()
const allCurrentRefinements = useAisCurrentRefinements({}, scopedId("all"))

const getTotalCurrentRefinements = () => {
  const maybeItems = (allCurrentRefinements as any)?.items
  const items = Array.isArray(maybeItems)
    ? maybeItems
    : Array.isArray(maybeItems?.value)
    ? maybeItems.value
    : []
  return items.reduce(
    (acc, current) => acc + (current?.refinements?.length ?? 0),
    0
  )
}

const handleCurrentRefinementClose = (
  items: any[],
  item: any,
  refinement: any
) => {
  if (getTotalCurrentRefinements() === 1) algoliaReset?.()
  item.refine(refinement)
}

const onClickedFilter = (item: string, category: string) => {
  if (item) {
    event.value = {
      event: selectContentEvent.selectContent,
      action: selectContentAction.filter,
      category: category.toLowerCase(),
      label: t(`filters.utils.availability.${item}`)
    }

    sendEvent({ clearEcommerce: false })
  }
}

const { nbHits, getFilterOptionResultCount } = useSwiftSearchHelper()

const closeAllModals = () => {
  openBrands(false)
  openCategories(false)
  openFilters(false)
}
</script>

<template>
  <FiltersUtilsModalWrapper
    :name="FILTERS_MODAL"
    :header="{
      title: $t('filters.utils.modalHead.filter')
    }"
    @onClose="openFilters(false)"
  >
    <div class="filters-modal__form flex flex-grow flex-col px-4">
      <FiltersUtilsModalContent
        :btn="{
          type: 'submit',
          text: $t('filters.utils.modals.filters.showResults'),
          nResults: 120
        }"
        :btn-shown="true"
      >
        <AlgoliaGroupsPrice />

        <FiltersUtilsGroup
          v-if="route.meta.page !== 'promotions-brand'"
          type="modal"
          name="brands"
          id-facet="brand.name"
          :title="$t('filters.utils.common.brands')"
          facetName="brand.name"
          @onClick="openBrands(true)"
        >
          <AisCurrentRefinements
            :id="scopedId('brand.name')"
            :included-attributes="['brand.name']"
            :class-names="{
              'ais-CurrentRefinements-item': 'flex flex-wrap gap-x-4',
              'ais-CurrentRefinements-label': 'hidden',
              'ais-CurrentRefinements-delete': 'ml-2 cursor-pointer',
              'ais-CurrentRefinements-category':
                'whitespace-normal bg-green-10 rounded-[100px] px-3 py-2 gap-x-2 mb-4 cursor-default'
            }"
          >
            <template v-slot="{ items }">
              <div class="flex flex-wrap gap-4">
                <template v-for="item in items" :key="item.attribute">
                  <ChipsMyChips
                    v-for="refinement of item.refinements"
                    class="bg-green-10 whitespace-nowrap rounded-[100px]"
                    :key="refinement.value"
                    :message="refinement.label"
                    :can-hide="false"
                    is-with-icon
                    @onClose="
                      () =>
                        handleCurrentRefinementClose(items, item, refinement)
                    "
                  />
                </template>
              </div>
            </template>
          </AisCurrentRefinements>
          <template #modal>
            <FiltersUtilsModalsBrands
              :primary-action="FILTER_ACTIONS.APPLY"
              @onApply="closeAllModals"
            />
          </template>
        </FiltersUtilsGroup>

        <FiltersUtilsGroup
          type="modal"
          name="categories"
          id-facet="mainCategory.name"
          :title="$t('filters.utils.common.categories')"
          facetName="mainCategory.name"
          @onClick="openCategories(true)"
        >
          <AisCurrentRefinements
            :id="scopedId('mainCategory.name')"
            :included-attributes="['mainCategory.name']"
            :class-names="{
              'ais-CurrentRefinements-item': 'flex flex-wrap gap-x-4',
              'ais-CurrentRefinements-label': 'hidden',
              'ais-CurrentRefinements-delete': 'ml-2 cursor-pointer',
              'ais-CurrentRefinements-category':
                'whitespace-normal bg-green-10 rounded-[100px] px-3 py-2 gap-x-2 mb-4 cursor-default'
            }"
          >
            <template v-slot="{ items }">
              <div class="flex flex-wrap gap-4">
                <template v-for="item in items" :key="item.attribute">
                  <ChipsMyChips
                    v-for="refinement of item.refinements"
                    class="bg-green-10 whitespace-nowrap rounded-[100px]"
                    :key="refinement.value"
                    :message="refinement.label"
                    :can-hide="false"
                    is-with-icon
                    @onClose="
                      () =>
                        handleCurrentRefinementClose(items, item, refinement)
                    "
                  />
                </template>
              </div>
            </template>
          </AisCurrentRefinements>
          <template #modal>
            <FiltersUtilsModalsCategories
              :primary-action="FILTER_ACTIONS.APPLY"
              @onApply="closeAllModals"
            />
          </template>
        </FiltersUtilsGroup>

        <FiltersUtilsGroup
          name="availability"
          id-facet="availability"
          content-class="pb-4 mb-16"
          :title="$t('filters.utils.common.availability')"
          :facetName="facetNames"
        >
          <AisToggleRefinement
            v-for="(item, index) in facetNames"
            :key="`refinement-toggle-${index}`"
            :attribute="facetNames[index]"
            :label="$t(`filters.utils.availability.${item}`)"
          >
            <template v-slot="{ canRefine, refine, value }">
              <FiltersUtilsOption
                :name="item"
                :checked="value.isRefined"
                :disabled="!canRefine"
                @clicked="
                  () => {
                    onClickedFilter(
                      item,
                      $t(`filters.utils.common.availability`)
                    )
                    if (getTotalCurrentRefinements() === 1 && value.isRefined) {
                      algoliaReset?.()
                    }
                    refine(value)
                  }
                "
                :text="$t(`filters.utils.availability.${item}`)"
                :n-results="
                  getFilterOptionResultCount(
                    canRefine,
                    value.isRefined,
                    value.count
                  )
                "
                type="toggle"
              />
            </template>
          </AisToggleRefinement>
        </FiltersUtilsGroup>
        <div
          class="
            elevation-fixed-button
            bg-white
            fixed
            bottom-0
            left-0
            right-0
            w-full
            p-4
          "
        >
          <UtilsButton
            @click="openFilters(false)"
            :text="
              $t('filters.utils.common.showResults', {
                number: nbHits ?? 0
              })
            "
            class="w-full"
          />
        </div>
      </FiltersUtilsModalContent>
    </div>
  </FiltersUtilsModalWrapper>
</template>

<style lang="scss">

.elevation-fixed-button {
  box-shadow: 0px -2px 12px 0px rgba(0, 0, 0, 0.08);
  -webkit-overflow-scrolling: touch;
}

.ais-CurrentRefinements-item {
  @apply flex flex-wrap gap-x-4;
}

.ais-CurrentRefinements-label {
  @apply hidden;
}
.ais-CurrentRefinements-delete {
  @apply ml-2 cursor-pointer;
}
.ais-CurrentRefinements-category {
  @apply bg-green-10 mb-4 cursor-default gap-x-2 whitespace-normal rounded-[100px] px-3 py-2;
}

</style>
